import type { LifecycleStateLabel } from "@/api/projects";
import { ACTIVE_RUN_STATES } from "@/api/runs";
import { STATE_LABEL } from "@/components/runs/run-state-label";

/**
 * **The lifecycle rail's model (VIS/V1).** Where a run in flight currently is,
 * derived from its lifecycle status and nothing else.
 *
 * ---
 *
 * ## The rail is a WALK of the state machine, not a list of state names
 *
 * The obvious implementation — iterate `ACTIVE_RUN_STATES` and mark everything
 * before the current status as done — is wrong, and wrong in the direction this
 * unit exists to avoid: it asserts a history the run does not have.
 *
 * `cached` is not a step on the way to `running`. The lifecycle
 * (`flowstack.lifecycle.state_machine._LEGAL_TRANSITIONS`) has exactly one
 * branch, and it is at the very first state:
 *
 *     validating ──▶ cached ─────────────────────────────────────▶ completed
 *          └──────▶ staging_inputs ──▶ dispatched ──▶ running
 *                        ──▶ collecting_outputs ──▶ validating_outputs ──▶ completed
 *
 * A run served from cache NEVER stages inputs, never dispatches and never runs;
 * a run that dispatched never passes through `cached`. So a single flat rail
 * over the seven active labels would tell a cached run that staging, dispatch
 * and execution are "still to come" — five stages that will not happen — and
 * would tell a running run it had already been "cached". Both are claims about
 * a run that the status contradicts.
 *
 * Hence {@link LIFECYCLE_PATHS}: two walks, and the run's status picks which
 * one it is on. Each is a legal walk of {@link LIFECYCLE_SUCCESSORS}, and
 * `run-lifecycle-rail.test.ts` proves it rather than trusting the ordering
 * above to have been typed correctly.
 *
 * ## While the run is still `validating`, the branch has NOT been taken
 *
 * The paragraph above says "the run's status picks which walk it is on", and
 * for six of the seven active states that is true. For the seventh it is not,
 * and the gap is the same class of overclaim this module exists to avoid
 * (raised as an advisory on #1222): `validating` is the state BEFORE the fork,
 * so it carries no information about which successor follows. Selecting the
 * dispatch walk there tells a run that is about to hit the cache that staging,
 * dispatch and execution are coming; selecting the cache walk makes the inverse
 * error for every run that actually dispatches.
 *
 * So a `validating` run gets a fourth mark. The two walks share exactly two
 * stages — `validating` and `completed` ({@link ON_EVERY_WALK}) — and those two
 * are certain whichever way the fork goes. Every stage between them is drawn
 * `undecided` rather than `ahead`: shown, so the reader still sees the shape of
 * the pipeline, but explicitly not yet a claim about THIS run. {@link
 * railCaption} names the other branch in words, because a dimmed stage on its
 * own does not say what the alternative is.
 *
 * ## Why `ACTIVE_RUN_STATES` is still the source of truth
 *
 * The dispatch's constraint — "do not hand-write a parallel list of state
 * names; if the rail and the polling gate can disagree, they eventually will" —
 * is discharged by {@link lifecycleRail} returning `null` for anything outside
 * `ACTIVE_RUN_STATES`, and by the test asserting that the two paths' non-
 * terminal stages are EXACTLY that set. Add an eighth active state to
 * `api/runs.ts` without placing it on a path and the suite goes red; the rail
 * cannot silently fall behind the thing that decides whether to keep polling.
 *
 * ## Terminal runs get no rail at all
 *
 * `lifecycleRail` is `null` for the five terminal labels, and that is a
 * decision about honesty rather than about scope.
 *
 * A settled run's status alone cannot reconstruct the path it took. `completed`
 * is reachable from `cached` AND from `validating_outputs`, so a completed run
 * rendered on the dispatch rail would claim it staged, dispatched and ran —
 * unknowable here, and false for every cache hit. The four `failed_*` labels
 * are worse: `failed_system` is reachable from `staging_inputs`, `dispatched`
 * OR `running`, so a rail would have to pick one of three stages to mark as
 * where the run died and would be wrong about two of them.
 *
 * The transition history that DOES answer this is real and already surfaced —
 * `GET /api/runs/{id}/logs` renders the append-only `run_transitions` audit
 * trail, which is the Logs tab. So the rail declines the question and the run
 * detail page points at the surface that can answer it.
 */

/**
 * Legal forward transitions, mirroring
 * `flowstack.lifecycle.state_machine._LEGAL_TRANSITIONS`.
 *
 * A mirror rather than an import, for the same reason `LifecycleStateLabel` in
 * `api/projects.ts` is one — the browser cannot read Python. What keeps the two
 * from drifting is `tests/lifecycle/test_lifecycle_rail_mirror.py`, which parses
 * THIS map out of THIS file and asserts it equals the state machine's, so the
 * copy is checked on every CI run rather than trusted.
 *
 * Only the forward edges matter here; the rail never renders a failure sink
 * (see the file docstring), but they are kept so the mirror test compares the
 * whole graph rather than a curated slice of it.
 */
export const LIFECYCLE_SUCCESSORS: Record<LifecycleStateLabel, readonly LifecycleStateLabel[]> = {
  validating: ["cached", "staging_inputs", "failed_validation"],
  cached: ["completed"],
  staging_inputs: ["dispatched", "failed_validation", "failed_system"],
  dispatched: ["running", "failed_system"],
  running: ["collecting_outputs", "failed_system", "failed_contract"],
  collecting_outputs: ["validating_outputs", "failed_contract"],
  validating_outputs: ["completed", "failed_scientific", "failed_contract"],
  completed: [],
  failed_validation: [],
  failed_system: [],
  failed_scientific: [],
  failed_contract: [],
};

/** Which of the state machine's two walks a run is on. */
export type RailPathKind = "dispatch" | "cache";

/**
 * The two walks from `validating` to `completed`, in lifecycle order.
 *
 * Both end at `completed` — a terminal label deliberately included as the
 * rail's last stage, because a rail whose final stop is `validating_outputs`
 * would not show the reader what finishing looks like. It is always marked
 * `ahead`: {@link lifecycleRail} never runs for a terminal status, so the run
 * has by construction not reached it.
 */
export const LIFECYCLE_PATHS: Record<RailPathKind, readonly LifecycleStateLabel[]> = {
  dispatch: [
    "validating",
    "staging_inputs",
    "dispatched",
    "running",
    "collecting_outputs",
    "validating_outputs",
    "completed",
  ],
  cache: ["validating", "cached", "completed"],
};

/**
 * The stages that lie on EVERY walk, so they are certain before the fork.
 *
 * Derived from {@link LIFECYCLE_PATHS} rather than typed out, so a third walk
 * (or a reshaped one) narrows this automatically instead of leaving a stale
 * hand-written pair behind. Today: `validating` and `completed`.
 */
export const ON_EVERY_WALK: ReadonlySet<LifecycleStateLabel> = new Set(
  LIFECYCLE_PATHS.dispatch.filter((state) => LIFECYCLE_PATHS.cache.includes(state))
);

/**
 * The states from which the walk is not yet determined — the shared prefix the
 * two paths agree on, minus nothing. Today: just `validating`.
 *
 * Computed as the longest common prefix, because "which states come before the
 * fork" is a property of the paths and not a fact worth restating.
 */
export const BEFORE_THE_FORK: ReadonlySet<LifecycleStateLabel> = (() => {
  const shared: LifecycleStateLabel[] = [];
  for (
    let i = 0;
    i < Math.min(LIFECYCLE_PATHS.dispatch.length, LIFECYCLE_PATHS.cache.length);
    i++
  ) {
    if (LIFECYCLE_PATHS.dispatch[i] !== LIFECYCLE_PATHS.cache[i]) break;
    shared.push(LIFECYCLE_PATHS.dispatch[i]);
  }
  return new Set(shared);
})();

/**
 * A stage's relation to where the run is now.
 *
 * `passed` and `ahead` are both FACTS about a walk whose order the state
 * machine fixes, while `current` is the one the status states directly. Nothing
 * here is an estimate, which is why the rail carries no "percent complete" —
 * the walk's steps are not equal in duration and pretending otherwise would
 * turn six facts into one guess.
 *
 * `undecided` is the fourth, and it is a claim the rail deliberately declines
 * to make: the run is still before the fork, so this stage is on ONE of the two
 * walks and nothing yet says which. It is not a weaker `ahead` — `ahead` asserts
 * "this run will reach here", and that assertion is exactly what is unavailable
 * while a run is `validating`.
 */
export type RailMark = "passed" | "current" | "ahead" | "undecided";

export interface RailStage {
  state: LifecycleStateLabel;
  /** Display text, from the one spelling in `run-state-label.ts`. */
  label: string;
  mark: RailMark;
}

export interface LifecycleRail {
  /** Which walk the run is on — the caption says so, since it is not obvious
   * from the stages alone that the other five were skipped rather than
   * pending. Meaningful only once {@link branchTaken} is true; before that it
   * is the walk being SHOWN, not the walk the run is on. */
  path: RailPathKind;
  /** False while the run is still before the fork (`validating`), when the
   * stages between the shared endpoints are `undecided` rather than `ahead`. */
  branchTaken: boolean;
  stages: readonly RailStage[];
  /** Index of the `current` stage in {@link stages}. Always in range. */
  currentIndex: number;
}

/**
 * The rail for `status`, or `null` when the run is not in flight.
 *
 * `null` covers every label outside `ACTIVE_RUN_STATES` — the five terminal
 * ones today. Callers render nothing rather than an empty rail: see the file
 * docstring for why a settled run's path is not reconstructible from its
 * status.
 */
export function lifecycleRail(status: LifecycleStateLabel): LifecycleRail | null {
  if (!ACTIVE_RUN_STATES.has(status)) return null;
  const branchTaken = !BEFORE_THE_FORK.has(status);
  // Before the fork, the dispatch walk is what gets DRAWN — it is the longer of
  // the two, so it shows the reader the shape of the pipeline — but every stage
  // on it that the cache walk does not share is marked `undecided`, and the
  // caption names the cache branch outright. After the fork the status selects
  // the walk outright, and `cached` is the only status on the short one.
  const path: RailPathKind = status === "cached" ? "cache" : "dispatch";
  const walk = LIFECYCLE_PATHS[path];
  const currentIndex = walk.indexOf(status);
  // Unreachable while the test below holds (every active label sits on exactly
  // one walk), and a thrown error would take the whole run page down over a
  // cosmetic rail. Degrade to "no rail" instead.
  if (currentIndex < 0) return null;
  return {
    path,
    branchTaken,
    currentIndex,
    stages: walk.map((state, index) => ({
      state,
      label: STATE_LABEL[state],
      mark:
        index < currentIndex
          ? "passed"
          : index === currentIndex
            ? "current"
            : branchTaken || ON_EVERY_WALK.has(state)
              ? "ahead"
              : "undecided",
    })),
  };
}

/**
 * What the rail's caption says about the walk this run is on.
 *
 * The cache walk needs the sentence: a reader who knows the pipeline will
 * notice `staging inputs`, `dispatched` and `running` are missing from the rail
 * and the honest reason is that they will never happen, not that the rail is
 * incomplete. The dispatch walk gets the shorter note pointing at the Logs tab,
 * which is where the actual per-transition timestamps live.
 *
 * Before the fork there is a third caption, and it carries the whole weight of
 * the `undecided` mark: a dimmed stage says "not asserted" but cannot say WHAT
 * the alternative is, so the sentence names the cache branch outright. Without
 * it the rail would still be quietly implying a dispatch this run may never do.
 */
export function railCaption(path: RailPathKind, branchTaken = true): string {
  if (!branchTaken) {
    return "This run is still validating, so it has not taken the lifecycle's branch yet: it either hits the cache and completes from there, or walks the dispatch path shown. The dimmed stages are that path — the shape of the pipeline, not yet a claim about this run. Per-transition timestamps are on the Logs tab.";
  }
  return path === "cache"
    ? "This run was served from the cache, so it takes the lifecycle's cache branch — it never stages inputs, dispatches or runs. Per-transition timestamps are on the Logs tab."
    : "Stages the lifecycle guarantees in this order; the rail marks which one the run reports being in, not how long each takes. Per-transition timestamps are on the Logs tab.";
}
